import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

/**
 * usePlannerSubtasks — EMBEDDED model (matches mobile).
 *
 * Subtasks live on each task row: tasks.subtasks = [{id, title, completed}].
 * Every row is independent; recurring occurrences receive fresh unchecked
 * copies from the parent at generation time (backend _build_instance_row).
 */
export function usePlannerSubtasks(userId) {
  const [overrides, setOverrides] = useState({});
  const cacheRef = useRef({});

  /** {taskKey, items, checkedIds} for one task row (taskKey === row id now). */
  const getForTask = useCallback((task) => {
    if (!task?.id) return { taskKey: null, items: [], checkedIds: [] };
    const arr = overrides[task.id] ?? task.subtasks ?? cacheRef.current[task.id] ?? [];
    cacheRef.current[task.id] = arr;
    return {
      taskKey: task.id,
      items: arr.map((s) => ({ id: s.id, title: s.title })),
      checkedIds: arr.filter((s) => s.completed).map((s) => s.id),
    };
  }, [overrides]);

  const write = useCallback(async (rowId, next, prev) => {
    setOverrides((m) => ({ ...m, [rowId]: next }));
    cacheRef.current[rowId] = next;
    const { error } = await supabase.from('tasks').update({ subtasks: next }).eq('id', rowId);
    if (error) {
      setOverrides((m) => ({ ...m, [rowId]: prev }));
      cacheRef.current[rowId] = prev;
      toast.error("Couldn't save sub-tasks. Please try again.");
    }
  }, []);

  /** Full replace of the row's checklist (keeps completed for kept ids). */
  const saveItems = useCallback(async (rowId, items) => {
    const prev = cacheRef.current[rowId] ?? [];
    const next = (items || []).map((i) => ({
      id: i.id,
      title: i.title,
      completed: prev.find((p) => p.id === i.id)?.completed || false,
    }));
    await write(rowId, next, prev);
  }, [write]);

  /** Tick/untick one sub-task on one row. (taskKey kept for signature compat) */
  const toggleCheck = useCallback(async (taskKey, rowId, subId, checked) => {
    const prev = cacheRef.current[rowId] ?? [];
    const next = prev.map((s) => (s.id === subId ? { ...s, completed: checked } : s));
    await write(rowId, next, prev);
  }, [write]);

  const taskKeyFor = (task) => task?.id;

  return { subtasksMap: {}, getForTask, saveItems, toggleCheck, taskKeyFor };
}
